'use client'

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'

import { TPaymentMethod, ZPaymentMethod } from '@/entities/contract'
import { isNilOrEmptyString } from '@/shared/lib'

export const PaymentMethod = ZPaymentMethod.enum

type PaymentMethodState = {
  paymentMethod: TPaymentMethod
  isSelected: (paymentMethod: TPaymentMethod) => boolean
  updatePaymentMethod: (paymentMethod: string) => void
}

export const PaymentMethodContext = createContext<PaymentMethodState | null>(
  null,
)

export const PaymentMethodProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const [paymentMethod, setPaymentMethod] = useState<TPaymentMethod>(
    PaymentMethod.ACCOUNT,
  )

  const isSelected = useCallback(
    (method: TPaymentMethod) => {
      return paymentMethod === method
    },
    [paymentMethod],
  )

  const update = (method: string) => {
    if (isNilOrEmptyString(method)) {
      return
    }

    setPaymentMethod(method as TPaymentMethod)
  }

  const value = useMemo(
    () => ({
      paymentMethod,
      isSelected,
      updatePaymentMethod: update,
    }),
    [isSelected, paymentMethod],
  )

  return (
    <PaymentMethodContext.Provider value={value}>
      {children}
    </PaymentMethodContext.Provider>
  )
}

export const usePaymentMethod = () => {
  const context = useContext(PaymentMethodContext)

  if (!context) {
    throw new Error(
      'usePaymentMethod must be used within a PaymentMethodProvider',
    )
  }

  return context
}
